import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

/** @param {string | { ticketId: string, silent?: boolean }} arg */
function normalizeGetTicketByIdArg(arg) {
  if (arg == null || arg === '') return { ticketId: '', silent: false };
  if (typeof arg === 'object' && arg !== null && arg.ticketId != null) {
    return { ticketId: String(arg.ticketId), silent: Boolean(arg.silent) };
  }
  return { ticketId: String(arg), silent: false };
}

export const getTicketById = createAsyncThunk(
  'ticket/getTicketById',
  async (arg, { getState, rejectWithValue }) => {
    const { ticketId } = normalizeGetTicketByIdArg(arg);
    if (!ticketId) return rejectWithValue('Missing ticketId');
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;

      const response = await axios.get(
        `${ROOT_URL}/ticket/get-ticket/${ticketId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  /** Last ticket id we successfully loaded; used for stale-while-revalidate revisit UX */
  lastFetchedTicketId: null,
  /** Fulfilled ticket payloads keyed by ticket id so switching away and back does not force cold load */
  cacheByTicketId: {},
  data: {
    ticket: null,
    messages: []
  }
};

const getTicketByIdSlice = createSlice({
  name: 'getTicketById',
  initialState,
  reducers: {
    resetTicketState: (state) => {
      state.loading = false;
      state.error = null;
      state.lastFetchedTicketId = null;
      state.cacheByTicketId = {};
      state.data = {
        ticket: null,
        messages: []
      };
    },
    updateTicketSubject: (state, action) => {
      if (state.data.ticket) {
        state.data.ticket.subject = action.payload.subject;
      }
    },
    updateTicketData: (state, action) => {
      if (state.data.ticket) {
        state.data.ticket = { ...state.data.ticket, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTicketById.pending, (state, action) => {
        const { ticketId: idStr, silent } = normalizeGetTicketByIdArg(
          action.meta.arg
        );
        const cached = idStr && state.cacheByTicketId[idStr];
        const ticketId = state.data?.ticket?._id;
        const inMemorySame =
          idStr &&
          ticketId != null &&
          String(ticketId) === idStr &&
          state.lastFetchedTicketId != null &&
          String(state.lastFetchedTicketId) === idStr;
        if (cached) {
          state.data = cached;
        }
        // Background refreshes must not flash skeletons in detail/header UI.
        state.loading = silent || cached || inMemorySame ? false : true;
        state.error = null;
      })
      .addCase(getTicketById.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        const { ticketId: argId } = normalizeGetTicketByIdArg(action.meta.arg);
        const tid =
          action.payload.data?.ticket?._id ?? argId ?? null;
        const idStr = tid != null ? String(tid) : '';
        if (idStr) {
          state.cacheByTicketId[idStr] = action.payload.data;
          state.lastFetchedTicketId = idStr;
        }
      })
      .addCase(getTicketById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase('updateTicket/fulfilled', (state, action) => {
        if (state.data.ticket && action.payload.data) {
          state.data.ticket = { ...state.data.ticket, ...action.payload.data };
        }
      });
  },
});

export const { 
  resetTicketState, 
  updateTicketSubject, 
  updateTicketData 
} = getTicketByIdSlice.actions;

export default getTicketByIdSlice.reducer;
